import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { captureException } from '@sentry/react';
import { QUERY_DELIVERY_PROFILE_ASSIGNMENTS } from '../graphql';
import { useDeliveryProfiles, useUpsertDeliveryProfile, whereForScope } from '../hooks';
import { profileRows, shipperOptions } from './utils';

// The grid's rows, the profiles behind them (Edit opens one), and the upsert behind Create / Edit.
export const useProfilesGrid = (scope, { onError } = {}) => {
    const where = whereForScope(scope);

    const { deliveryProfiles, loading } = useDeliveryProfiles(scope, { onError });
    // Same variables as the profile page's shipper directory, so both read one cached list.
    const { data: assignmentsData, loading: assignmentsLoading } = useQuery(QUERY_DELIVERY_PROFILE_ASSIGNMENTS, {
        variables: { where: { delivery_profile: where || {} } },
        skip: !where,
        onError: (e) => {
            captureException(e);
            onError?.('Failed to load profile assignments');
        },
    });
    const upsertProfile = useUpsertDeliveryProfile(scope, { onError });

    const rows = useMemo(
        () => profileRows(deliveryProfiles, assignmentsData?.results),
        [deliveryProfiles, assignmentsData]
    );
    const shippers = useMemo(() => shipperOptions(assignmentsData?.results), [assignmentsData]);
    const profilesById = useMemo(
        () => Object.fromEntries(deliveryProfiles.map((profile) => [profile.delivery_profile_id, profile])),
        [deliveryProfiles]
    );

    return { rows, shipperOptions: shippers, profilesById, upsertProfile, loading: loading || assignmentsLoading };
};
